import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Services } from '../../api/Services';
import AppLoader from '../../components/common/AppLoader';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { BdaService, BdaServiceApplicationProperty, BdaServiceProperty, BdaServiceStaticProperty, BdaServiceTuningProperty, Framework, TuningPropertyMapping } from '../../models/BdaService';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Card, Col, Container, Form, Row, Modal, Breadcrumb, Table } from 'react-bootstrap';
import { Frameworks } from '../../api/Frameworks';
import ScriptEditor from '../../components/common/ScriptEditor';
import { debounce } from 'lodash';
import { getPropertyIcon, setObjectType } from '../../utils';
import { MappingsEditor } from '../../components/generics/MappingsEditor';
import IconButton from '../../components/common/IconButton';
import { Select } from 'antd';
import { editor } from 'monaco-editor';
import { Option } from '../../models/generics';

const SERVICE_NAME_REGEX = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;

interface FrameworkOption {
  label: string;
  value: number;
  image: string;
  trailing: string;
}

interface ValidationError {
  key: string;
  message: string;
}

export default function ServiceEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const [validated, setValidated] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [importMode, setImportMode] = useState(false);

  const [service, setService] = useState<BdaService>();
  const [frameworks, setFrameworks] = useState<Framework[]>();
  const [frameworkOptions, setFrameworkOptions] = useState<FrameworkOption[]>();
  const [newProperty, setNewProperty] = useState(new BdaServiceProperty('application'));
  const [editing, setEditing] = useState<number>();

  const [modesOptions, setModesOptions] = useState<Option[]>();
  const [areasOptions, setAreasOptions] = useState<Option[]>();
  const [acceptedAreas, setAcceptedAreas] = useState<string[]>();
  const [acceptedModes, setAcceptedModes] = useState<string[]>();

  const [definitionErrors, setDefinitionErrors] = useState<editor.IMarker[]>();
  const [nameError, setNameError] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>();

  const serviceJSON = useMemo(() => {
    return JSON.stringify(service, (_, value) => typeof value === 'undefined' ? null : value, "\t");
  }, [service]);


  useEffect(() => {
    if (id) retrieveService();
    else setService(new BdaService());

    initFrameworks();
    initModes();
    initAreas();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (service) validateInformation(service);
    // eslint-disable-next-line
  }, [service]);

  const retrieveService = () => {
    Services.getService(+id!)
      .then(
        ({ data: service }) => setService(service),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('service-error', message, `Service Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initModes = () => {
    Services.getModes()
      .then(
        ({ data: modes }) => {
          setAcceptedModes(modes);
          setModesOptions(
            modes.map(mode => ({
              label: mode.split("_").join(" "),
              value: mode,
            }))
          )
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('modes-error', message, `Modes Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initAreas = () => {
    Services.getAreas()
      .then(
        ({ data: areas }) => {
          setAcceptedAreas(areas);
          setAreasOptions(
            areas.map(area => ({
              label: area.split("_").join(" "),
              value: area,
            }))
          )
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('areas-error', message, `Areas Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initFrameworks = () => {
    Frameworks.getFrameworks()
      .then(
        ({ data: frameworks }) => {
          setFrameworks(frameworks);
          setFrameworkOptions(
            frameworks.map(f => ({
              label: f.name,
              value: f.id,
              image: f.imageUrl,
              trailing: f.version,
            }))
          );
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('frameworks-error', message, `Frameworks Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const handlePropertyModalClose = () => setShowPropertyModal(false);
  const openPropertyModal = () => setShowPropertyModal(true);
  const addProperty = () => {
    var newProperties;
    if (editing !== undefined) {
      newProperties = [...service!.properties];
      newProperties[editing] = newProperty;
    }
    else newProperties = service!.properties.concat(newProperty);
    setService({
      ...service!,
      properties: newProperties,
    });
    setNewProperty(new BdaServiceApplicationProperty());
    setShowPropertyModal(false);
  }

  const toggleImportMode = () => setImportMode(!importMode);

  const cancel = () => navigate('/services');

  const handleFieldChange = (field: keyof BdaService) => (evt: any) => {
    const value = (field === 'mode' || field === 'area' || field === 'framework' || field === 'tags') ? evt : evt.target.value;
    setService({
      ...service!,
      [field]: value,
    });
    if (field === "name") {
      if (!SERVICE_NAME_REGEX.test(value) || value.length > 30) setNameError(true);
      else setNameError(false);
    }
  }

  const handlePropertyChange = (field: string, checkbox?: boolean) => (evt: any) => {
    const value = (field === 'type' || field === 'mappings') ? evt : checkbox ? evt.target.checked : evt.target.value;
    setNewProperty({
      ...newProperty,
      [field]: value,
    });
  }

  const submitService = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.preventDefault();
    evt.stopPropagation();

    setValidated(true);

    if (form.checkValidity() === true) sendRequest();
  }

  const submitDefinition = () => {
    if (errors?.length || definitionErrors?.length) return;

    sendRequest();
  }

  const sendRequest = () => {
    showLoader();

    const promise = id
      ? Services.updateService(service!)
      : Services.createService(service!);

    promise
      .then(
        ({ data: newService }) => {
          hideLoader();
          showToast('services-success', `Service ${service!.name} ${id ? 'updated' : 'created'} successfully.`, 'Success', 'success');
          navigate(`/services/${newService.id}`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('services-error', message, `Could not ${id ? 'update' : 'create'} service${status ? ` (${status})` : ''}`, 'danger');
        }
      );
  }

  const editProperty = (index: number) => () => {
    setEditing(index);
    setNewProperty({ ...service!.properties[index] });
    setShowPropertyModal(true);
  }

  const cloneProperty = (index: number) => () => {
    const cloned = { ...service!.properties[index] };
    cloned.key += `-${Math.random().toString(36).replace(/[^a-z]+/g, '').substring(0, 5)}`;
    setService({
      ...service!,
      properties: service!.properties.concat(cloned),
    });
  }

  const deleteProperty = (index: number) => () => {
    const newProperties = [...service!.properties];
    newProperties.splice(index, 1);
    setService({
      ...service!,
      properties: newProperties
    });
  }

  const renderServiceProperties = () => {
    if (!service!.properties.length) return (
      <Alert className="py-2" variant="primary">
        <FontAwesomeIcon icon={solid('info-circle')} />
        <span className="ms-2">No properties found.</span>
      </Alert>
    )

    return (
      <Table className="mb-0">
        <thead>
          <tr>
            <th style={{ width: 20 }}></th>
            <th>Key</th>
            <th className="text-center">Type</th>
            <th className="text-center">Default Value</th>
            <th className="text-center">Mandatory</th>
            <th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {service!.properties.map((property, index) => (
            <tr key={`prop-${property.key}`}>
              <td>
                <FontAwesomeIcon icon={getPropertyIcon(property.type)} />
              </td>
              <td>{property.key}</td>
              <td className="text-center">{property.valueType}</td>
              <td className="text-center">{property.defaultValue}</td>
              <td className="text-center">{property.mandatory && <FontAwesomeIcon icon={solid('check-square')} />}</td>
              <td className="text-center">
                <IconButton type="button" onClick={editProperty(index)} tooltip="Edit">
                  <FontAwesomeIcon icon={solid('pen')} />
                </IconButton>
                <IconButton type="button" className="ms-2" onClick={cloneProperty(index)} tooltip="Clone">
                  <FontAwesomeIcon icon={solid('copy')} />
                </IconButton>
                <IconButton type="button" className="ms-2" onClick={deleteProperty(index)} tooltip="Remove">
                  <FontAwesomeIcon icon={solid('trash')} />
                </IconButton>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    )
  }

  const renderServiceForm = () => {
    return (
      <Form noValidate validated={validated} onSubmit={submitService}>
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="service-name" className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control size="sm" placeholder="Service Name *" value={service!.name} onChange={handleFieldChange('name')} required />
                <Form.Control.Feedback type="invalid">Service name is required.</Form.Control.Feedback>
                {nameError && <Form.Control.Feedback type="invalid" className="d-block">Service name can include just numbers, letters and dash ('-') and must be under 30 characters.</Form.Control.Feedback>}
              </Form.Group>

              <div className="mb-3">
                <Form.Label>Mode</Form.Label>
                <Select
                  allowClear
                  className="w-100"
                  value={service!.mode}
                  placeholder="Select Service Mode"
                  options={modesOptions}
                  onChange={handleFieldChange("mode")}
                />
                {validated && !service!.mode && <Form.Control.Feedback className="d-block" type="invalid">Service mode is required.</Form.Control.Feedback>}
              </div>

              <Form.Label>Area</Form.Label>
              <Select
                allowClear
                className="w-100"
                placeholder="Select service area"
                options={areasOptions}
                value={service!.area}
                onChange={handleFieldChange('area')}
              />
              {validated && !service!.area && <Form.Control.Feedback className="d-block" type="invalid">Service area is required.</Form.Control.Feedback>}
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3" controlId="service-description">
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={3} placeholder="Service description" value={service!.description} onChange={handleFieldChange('description')}></Form.Control>
              </Form.Group>

              <Form.Label>Framework</Form.Label>
              <Select
                allowClear
                className="w-100"
                placeholder="Select service framework"
                loading={!frameworkOptions}
                value={service!.framework}
                onChange={handleFieldChange('framework')}
              >
                {frameworkOptions?.map(option => (
                  <Select.Option key={option.value} value={option.value} label={option.label}>
                    <div className="d-flex align-items-center">
                      <div className="framework-image me-2" style={{ backgroundImage: `url(${option.image})` }}></div>
                      <h6 className="mb-0">{option.label}</h6>
                      <span className="text-muted ms-3">{option.trailing}</span>
                    </div>
                  </Select.Option>
                ))}
              </Select>
              {validated && !service!.framework && <Form.Control.Feedback className="d-block" type="invalid">Service framework is required.</Form.Control.Feedback>}
            </Col>
          </Row>

          <Row className="gx-2">
            <Col>
              <Form.Group controlId="service-url" className="mb-3">
                <Form.Label>Url</Form.Label>
                <Form.Control size="sm" placeholder="Service URL *" type="url" value={service!.url} onChange={handleFieldChange('url')} required />
                <Form.Control.Feedback type="invalid">Service Url is required.</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group controlId="service-version">
                <Form.Label>Version</Form.Label>
                <Form.Control size="sm" placeholder="Service Version *" value={service!.version} onChange={handleFieldChange('version')} required />
                <Form.Control.Feedback type="invalid">Service Version is required.</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group controlId="service-tags">
            <Form.Label>Tags</Form.Label>
            <Select mode="tags" className="w-100" placeholder="Service Tags" value={service!.tags} onChange={handleFieldChange('tags')} />
          </Form.Group>

          <hr />

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Service Properties</h6>

            <Button size="sm" variant="light" className="border" onClick={openPropertyModal}>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="ms-2">Add property</span>
            </Button>
          </div>
          {renderServiceProperties()}

        </Card.Body>

        <div className="d-flex border-top p-2 px-3 justify-content-between">
          <Button type="button" variant="light" className="border" onClick={cancel}>Cancel</Button>
          <Button type="submit" variant="success">
            <FontAwesomeIcon icon={solid('save')} />
            <span className="ms-2">Save</span>
          </Button>
        </div>
      </Form>
    )
  }

  const validateInformation = function (service: BdaService) {
    const errors = [];
    if (!service.name || service.name === "") errors.push({ key: 'name', message: 'is required and cannot be empty' });
    else if (!SERVICE_NAME_REGEX.test(service.name) || service.name.length > 30) errors.push({ key: 'name', message: "can include just numbers, letters and dash ('-') and must be under 30 characters." });
    if (!service.mode || service.mode as string === "") errors.push({ key: 'mode', message: 'is required and cannot be empty' });
    else if (acceptedModes && !acceptedModes.includes(service.mode)) errors.push({ key: 'mode', message: `must be one of the following values: ${acceptedModes.join(', ')}` })
    if (!service.area || service.area === "") errors.push({ key: 'area', message: 'is required and cannot be empty' });
    else if (acceptedAreas && !acceptedAreas.includes(service.area)) errors.push({ key: 'area', message: `must be one of the following values: ${acceptedAreas.join(', ')}` })
    if (!service.version || service.version === "") errors.push({ key: 'version', message: 'is required and cannot be empty' });
    if (!service.framework) errors.push({ key: 'framework', message: 'is required and cannot be empty' });
    if (!service.url || service.url === "") errors.push({ key: 'url', message: 'is required and cannot be empty' });
    setErrors(errors);
  }

  // eslint-disable-next-line
  const debounceParsing = useCallback(
    debounce(function (value) {
      try {
        const service: BdaService = JSON.parse(value);
        // TODO should we improve this?
        if (!id) {
          delete service.id;
          delete service.ref;
          delete service.label;
          delete service.statusModel;
          service.properties.forEach(prop => delete prop.id);
        }

        setService(service);
      } catch (e) {
        console.log('JSON is not valid', e);
      }
    }, 1000),
    []
  );

  const handleJSONErrors = (errors: editor.IMarker[]) => setDefinitionErrors(errors)

  const renderServiceJSON = () => {
    return (
      <>
        <Row className="gx-0 align-items-start">
          <Col md={6}>
            <ScriptEditor
              value={serviceJSON}
              onChange={debounceParsing}
              onValidated={handleJSONErrors}
            />
          </Col>

          <Col md={6} className="p-2">
            {definitionErrors && definitionErrors.length > 0 && (
              <>
                <h6 className="text-danger">Definition Errors</h6>
                {
                  definitionErrors.map(({ message, startLineNumber, startColumn, endLineNumber, endColumn }, index) => (
                    <Alert className="py-2 mb-2" key={index} variant="danger">
                      <FontAwesomeIcon icon={solid('exclamation-circle')} />
                      <b className="ms-3">[{startLineNumber}:{startColumn} - {endLineNumber}:{endColumn}]</b>
                      <span className="ms-2">{message}</span>
                    </Alert>
                  ))
                }
              </>
            )}

            {errors && errors.length > 0 && (
              <>
                <h6 className="text-danger">Information Errors</h6>
                {
                  errors.map((err, index) => (
                    <Alert className="py-2 mb-2" key={index} variant="danger">
                      <FontAwesomeIcon icon={solid('exclamation-circle')} />
                      <b className="ms-3">{err.key}</b>
                      <span className="ms-2">{err.message}</span>
                    </Alert>
                  ))
                }
              </>
            )}

            {(!definitionErrors || definitionErrors.length === 0) && (!errors || errors.length === 0) && (
              <Alert variant="success">
                <FontAwesomeIcon icon={solid('check-circle')} />
                <span className="ms-3">Definition looks good.</span>
              </Alert>
            )}
          </Col>
        </Row>
        <div className="d-flex border-top p-2 px-3 justify-content-between">
          <Button type="button" variant="light" className="border" onClick={cancel}>Cancel</Button>
          <Button type="button" variant="success" onClick={submitDefinition}>
            <FontAwesomeIcon icon={solid('save')} />
            <span className="ms-2">Save</span>
          </Button>
        </div>
      </>
    )
  }

  const renderAsyncContent = () => {
    return service && frameworks
      ? importMode ? renderServiceJSON() : renderServiceForm()
      : (
        <div className="p-5 text-center">
          <AppLoader size={350} />
        </div>
      );
  }

  return (
    <Container className="mt-3">
      <Helmet>
        <title>Service {id ? `${id} Edit` : "Registration"}</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/services' }}>Services</Breadcrumb.Item>
        {id
          ? (
            <>
              <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/services/${id}` }}>{service?.name}</Breadcrumb.Item>
              <Breadcrumb.Item active>Service Edit</Breadcrumb.Item>
            </>
          )
          : <Breadcrumb.Item active>New Service</Breadcrumb.Item>
        }
      </Breadcrumb>

      <Card className="mb-3">
        <Row className="gx-0 border-bottom p-2 px-3 align-items-center">
          <Col>
            <h5 className="mb-1">{id ? 'Service Edit' : 'New Service'}</h5>
            <h6 className="text-muted mb-0">{id ? 'Edit existing service.' : 'Register new service below.'}</h6>
          </Col>

          <Button size="sm" variant="light" className="w-auto border" onClick={toggleImportMode}>
            <FontAwesomeIcon icon={importMode ? solid('edit') : solid('code')} />
            <span className="ms-2">{importMode ? 'Manual Definition' : 'Import Definition'}</span>
          </Button>
        </Row>

        {renderAsyncContent()}
      </Card>

      <ServicePropertyModal
        show={showPropertyModal}
        onHide={handlePropertyModalClose}
        onAdd={addProperty}
        onPropertyChange={handlePropertyChange}
        newProperty={newProperty}
      />
    </Container>
  )
}

const types = [
  { label: 'Application Property', value: 'application', icon: solid('play-circle') },
  { label: 'Tuning Property', value: 'tuning', icon: solid('wrench') },
  { label: 'Static Property', value: 'static', icon: solid('hdd') },
];

type ModalProps = {
  show: boolean;
  onHide: VoidFunction;
  onAdd: VoidFunction;
  newProperty: BdaServiceProperty;
  onPropertyChange: (key: string, checkbox?: boolean) => (value: any) => void;
}

const ServicePropertyModal = memo(({ show, onHide, onAdd, newProperty, onPropertyChange }: ModalProps) => {
  const [validated, setValidated] = useState(false);

  const handleMappingsChange = (newMappings: TuningPropertyMapping[]) => onPropertyChange('mappings')(newMappings);

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.preventDefault();
    evt.stopPropagation();
    setValidated(true);

    if (form.checkValidity() === true) {
      form.reset();
      setValidated(false);
      onAdd();
    }
  }

  const renderPropertyForm = () => {
    return (
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <div className="p-3">
          <Form.Group className="mb-3" controlId="new-property-key-fieldset">
            <Form.Label>Key</Form.Label>
            <Form.Control size="sm" placeholder="Property key" value={newProperty.key} onChange={onPropertyChange('key')} required />
            <Form.Control.Feedback type="invalid">Property Key is required</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="new-property-description-fieldset">
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" placeholder="Property description" rows={3} size="sm" value={newProperty.description || ""} onChange={onPropertyChange('description')} />
          </Form.Group>

          <Form.Group className="mb-3" controlId="new-property-type-fieldset">
            <Form.Label>Type</Form.Label>
            <Select className="w-100" value={newProperty.type} onChange={onPropertyChange('type')}>
              {types.map((option, index) => (
                <Select.Option key={index} value={option.value} label={option.value}>
                  <FontAwesomeIcon icon={option.icon} fixedWidth />
                  <span className="ms-2">{option.label}</span>
                </Select.Option>
              ))}
            </Select>
          </Form.Group>

          <div className="mb-2">
            <Form.Check
              inline
              label="Mandatory"
              type="checkbox"
              id="new-property-mandatory-checkbox"
              checked={newProperty.mandatory}
              onChange={onPropertyChange('mandatory', true)}
            />

            <Form.Check
              inline
              label="Invisible"
              type="checkbox"
              className="ms-3"
              id="new-property-invisible-checkbox"
              checked={newProperty.invisible}
              onChange={onPropertyChange('invisible', true)}
            />
          </div>

          <Form.Group className="mb-3" controlId="new-property-value-type-fieldset">
            <Form.Label>Value type</Form.Label>
            <Form.Select size="sm" value={newProperty.valueType} onChange={onPropertyChange('valueType')}>
              <option value="STRING">String</option>
              <option value="BOOLEAN">Boolean</option>
              <option value="INT">Integer</option>
              <option value="DOUBLE">Double</option>
            </Form.Select>
          </Form.Group>

          {newProperty.valueType === 'BOOLEAN'
            ? (
              <Form.Check
                label={`Default Value: ${newProperty.defaultValue === true}`}
                type="checkbox"
                id="new-property-default-value-checkbox"
                value={newProperty.defaultValue || false}
                checked={newProperty.defaultValue}
                onChange={onPropertyChange('defaultValue', true)}
                className="mb-2"
              />
            )
            : (
              <Form.Group className="mb-3" controlId="new-property-default-value-fieldset">
                <Form.Label>Default Value</Form.Label>
                <Form.Control
                  placeholder="Property default value"
                  size="sm"
                  value={newProperty.defaultValue || ""}
                  type={newProperty.valueType === 'STRING' ? 'text' : 'number'}
                  step={
                    newProperty.valueType === 'DOUBLE'
                      ? 0.01
                      : 1
                  }
                  onChange={onPropertyChange('defaultValue')}
                />
              </Form.Group>
            )
          }

          {newProperty.type === 'application' && setObjectType<BdaServiceApplicationProperty>(newProperty) && (
            <div className="mb-2">
              <Form.Check
                inline
                label="Input Data"
                type="checkbox"
                id="new-property-input-data-checkbox"
                checked={newProperty.inputData}
                onChange={onPropertyChange('inputData', true)}
              />

              <Form.Check
                inline
                label="Output Data"
                type="checkbox"
                className="ms-3"
                id="new-property-output-data-checkbox"
                checked={newProperty.outputData}
                onChange={onPropertyChange('outputData', true)}
              />
            </div>
          )}

          {newProperty.type === 'static' && setObjectType<BdaServiceStaticProperty>(newProperty) && (
            <>
              <Form.Check
                label="Externalized"
                type="checkbox"
                id="new-property-externalized-checkbox"
                checked={newProperty.externalized}
                onChange={onPropertyChange('externalized', true)}
                className="mb-2"
              />

              <Form.Group controlId="new-property-url-fieldset">
                <Form.Label>Url</Form.Label>
                <Form.Control
                  size="sm"
                  type="url"
                  placeholder="Property url"
                  value={newProperty.uri || ""}
                  onChange={onPropertyChange('uri')}
                  required={newProperty.externalized}
                />
                <Form.Control.Feedback type="invalid">Property URL is required when property is externalized.</Form.Control.Feedback>
              </Form.Group>
            </>
          )}

          {newProperty.type === 'tuning' && setObjectType<BdaServiceTuningProperty>(newProperty) && (
            <>
              <Form.Group className="mb-3" controlId="new-property-measure-fieldset">
                <Form.Label>Measure</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="Property Measure"
                  value={newProperty.measure || ""}
                  onChange={onPropertyChange('measure')}
                />
              </Form.Group>

              <Form.Check
                label="Category"
                type="checkbox"
                id="new-property-category-checkbox"
                checked={newProperty.category}
                onChange={onPropertyChange('category', true)}
                className="mb-2"
              />

              <Form.Group className="mb-3" controlId="new-property-min-value-fieldset">
                <Form.Label>Min Value</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="Property Min Value"
                  value={newProperty.minValue || ""}
                  onChange={onPropertyChange('minValue')}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="new-property-max-value-fieldset">
                <Form.Label>Max Value</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="Property Max Value"
                  value={newProperty.maxValue || ""}
                  onChange={onPropertyChange('maxValue')}
                />
              </Form.Group>

              <Form.Label>Mappings</Form.Label>
              <MappingsEditor mappings={newProperty.mappings} onMappingsChange={handleMappingsChange} />
            </>
          )}
        </div>

        <div className="d-flex border-top justify-content-between p-2">
          <Button variant="light" type="button" size="sm" className="border" onClick={onHide}>
            Close
          </Button>
          <Button variant="success" type="submit" size="sm">
            <FontAwesomeIcon icon={solid('check')} />
            <span className="ms-2">Confirm</span>
          </Button>
        </div>
      </Form>
    )
  }

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>New property</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">{renderPropertyForm()}</Modal.Body>
    </Modal>
  )
})